import { ref, computed } from 'vue'
import { getAuth, onAuthStateChanged } from 'firebase/auth'

const status = ref(null)
const isLoading = ref(false)
const error = ref(null)
let authUnsub = null
let fetchPromise = null

export function useAgentOnboarding() {
  const steps = computed(() => status.value?.steps || [])
  const progress = computed(() => status.value?.progress ?? 0)
  const isComplete = computed(() => status.value?.isComplete === true)
  const showWizard = computed(() => status.value?.showWizard === true)
  const showChecklist = computed(() => status.value?.showChecklist === true)
  const wizardStep = computed(() => status.value?.wizardStep || 'profile')
  const wizardComplete = computed(() => status.value?.wizardComplete === true)
  const isAssociated = computed(() => status.value?.isAssociated === true)

  async function fetchOnboardingStatus() {
    if (fetchPromise) return fetchPromise

    fetchPromise = (async () => {
      isLoading.value = true
      error.value = null
      try {
        const auth = getAuth()
        const user = auth.currentUser
        if (!user) return

        const token = await user.getIdToken()
        const res = await fetch('/api/agents/onboarding-status', {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error('No se pudo cargar el onboarding')
        const data = await res.json()
        if (!data.success) throw new Error(data.error || 'Error')
        status.value = data
      } catch (e) {
        console.error('[useAgentOnboarding]', e)
        error.value = e.message
      } finally {
        isLoading.value = false
        fetchPromise = null
      }
    })()

    return fetchPromise
  }

  async function markVisited(section) {
    try {
      const auth = getAuth()
      const user = auth.currentUser
      if (!user) return

      const token = await user.getIdToken()
      await fetch('/api/agents/onboarding', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ markVisited: section }),
      })
      await fetchOnboardingStatus()
    } catch (e) {
      console.error('[useAgentOnboarding] markVisited:', e)
    }
  }

  function initOnboardingAuth() {
    if (authUnsub) return
    const auth = getAuth()
    if (auth.currentUser) {
      fetchOnboardingStatus()
    } else {
      authUnsub = onAuthStateChanged(auth, (user) => {
        if (user) fetchOnboardingStatus()
      })
    }
  }

  function destroyOnboardingAuth() {
    if (authUnsub) {
      authUnsub()
      authUnsub = null
    }
  }

  return {
    status,
    steps,
    progress,
    isComplete,
    showWizard,
    showChecklist,
    wizardStep,
    wizardComplete,
    isAssociated,
    isLoading,
    error,
    fetchOnboardingStatus,
    markVisited,
    initOnboardingAuth,
    destroyOnboardingAuth,
  }
}

/** Singleton compartido entre Layout, Home y vistas del agente */
let sharedInstance = null

export function useSharedAgentOnboarding() {
  if (!sharedInstance) {
    sharedInstance = useAgentOnboarding()
  }
  return sharedInstance
}
